import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Flex, Text, Button, Input, Stack } from "@chakra-ui/react";
import { ChevronDown, Search, X } from "lucide-react";
import { inputStyle } from "@/pages/auth/AuthShell";
import { colors } from "@/theme/colors";

const nameOf = (w) => w.full_name || w.name || "";

// Shared condensed worker picker. Collapsed to one line; tap opens a searchable
// panel (bottom sheet on mobile). Pure UI — data comes in via props, so it works
// offline with whatever cached roster the caller already has.
// value is ALWAYS an array of worker ids (single-select uses a 0/1-length array).
export function WorkerPicker({ workers = [], value = [], onChange, multi = false, placeholder, testId, fallbackNames = {} }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const byId = useMemo(() => Object.fromEntries(workers.map((w) => [w.id, w])), [workers]);
  const label = (id) => (byId[id] ? nameOf(byId[id]) : fallbackNames[id] || "Unknown");
  const sorted = useMemo(() => [...workers].sort((a, b) => nameOf(a).localeCompare(nameOf(b))), [workers]);
  const term = q.trim().toLowerCase();
  const filtered = term ? sorted.filter((w) => nameOf(w).toLowerCase().includes(term)) : sorted;

  const close = () => { setOpen(false); setQ(""); };
  const toggle = (id) => {
    if (multi) onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
    else { onChange(value.includes(id) ? [] : [id]); close(); }
  };

  // Close on Escape without also closing a parent Modal (capture + stopPropagation).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); close(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = value.length
    ? value.slice(0, 2).map(label).join(", ") + (value.length > 2 ? ` +${value.length - 2}` : "")
    : null;

  return (
    <>
      <Flex as="button" type="button" onClick={() => setOpen(true)} data-testid={`${testId}-trigger`}
        w="full" align="center" justify="space-between" gap="2" px="3.5" py="3" borderRadius="10px"
        border="1.5px solid" borderColor={value.length ? colors.teal : colors.border}
        bg={value.length ? colors.tealTint : colors.white} textAlign="left"
        _hover={{ borderColor: colors.teal }}>
        <Text fontSize="sm" color={summary ? colors.textPrimary : colors.textSecondary} lineClamp={1}>
          {summary || placeholder || (multi ? "Select workers" : "Select worker")}
        </Text>
        <ChevronDown size={16} color={colors.textSecondary} style={{ flexShrink: 0 }} />
      </Flex>

      {open && createPortal(
        <Flex position="fixed" inset="0" zIndex={2147483200} align={{ base: "flex-end", md: "center" }}
          justify="center" data-testid={`${testId}-panel`}>
          <Box position="absolute" inset="0" bg="rgba(31,41,55,0.45)" onClick={close} />
          <Flex direction="column" position="relative" bg={colors.white} w="full" maxW={{ md: "440px" }}
            borderRadius={{ base: "16px 16px 0 0", md: "16px" }} maxH="80dvh" p="4" gap="3"
            pb={{ base: "calc(2.75rem + env(safe-area-inset-bottom, 0px))", md: "4" }}
            m={{ base: 0, md: 4 }} boxShadow="0 12px 32px rgba(31,41,55,0.16)">
            <Flex align="center" gap="2" px="3" borderRadius="10px" border="1.5px solid" borderColor={colors.border} bg={colors.white}>
              <Search size={16} color={colors.textSecondary} style={{ flexShrink: 0 }} />
              <Input {...inputStyle} border="none" px="0" height="44px" fontSize="md" _focus={{ boxShadow: "none" }}
                placeholder="Search by name…" value={q} onChange={(e) => setQ(e.target.value)}
                data-testid={`${testId}-search`} />
            </Flex>

            {multi && value.length > 0 && (
              <Flex gap="1.5" flexWrap="wrap">
                {value.map((id) => (
                  <Flex key={id} as="button" type="button" onClick={() => toggle(id)} data-testid={`${testId}-chip-${id}`}
                    align="center" gap="1" px="2.5" py="1" borderRadius="full" bg={colors.tealTint} color={colors.tealHover} fontSize="xs" fontWeight="600">
                    {label(id)} <X size={12} />
                  </Flex>
                ))}
              </Flex>
            )}

            <Stack gap="1.5" overflowY="auto" flex="1" minH="120px">
              {filtered.map((w) => {
                const checked = value.includes(w.id);
                return (
                  <Flex key={w.id} as="button" type="button" onClick={() => toggle(w.id)}
                    data-testid={`${testId}-opt-${w.id}`} w="full" align="center" gap="3"
                    px="3.5" py="3" borderRadius="10px" border="1.5px solid" textAlign="left" flexShrink={0}
                    borderColor={checked ? colors.teal : colors.border} bg={checked ? colors.tealTint : colors.white}>
                    <Box w="18px" h="18px" flexShrink={0} borderRadius={multi ? "4px" : "full"} border="2px solid"
                      borderColor={checked ? colors.teal : colors.border} bg={checked ? colors.teal : "transparent"}
                      display="flex" alignItems="center" justifyContent="center">
                      {checked && <Box w="7px" h="7px" borderRadius={multi ? "1px" : "full"} bg="white" />}
                    </Box>
                    <Text fontSize="sm" color={colors.textPrimary}>
                      {nameOf(w)}{w.job_role ? <Text as="span" color={colors.textSecondary}> · {w.job_role}</Text> : null}
                    </Text>
                  </Flex>
                );
              })}
              {filtered.length === 0 && (
                <Text fontSize="sm" color={colors.textSecondary} py="3" textAlign="center" data-testid={`${testId}-empty`}>
                  {workers.length === 0 ? "No workers found." : `No matches for “${q}”.`}
                </Text>
              )}
            </Stack>

            <Button onClick={close} data-testid={`${testId}-done`} bg={colors.teal} color="white" _hover={{ bg: colors.tealHover }}>
              Done{multi && value.length ? ` (${value.length})` : ""}
            </Button>
          </Flex>
        </Flex>,
        document.body
      )}
    </>
  );
}
